import { connect } from 'pwa-helpers';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit-html/directives/class-map.js';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import {
  areProductsUnavailable,
  getCart,
  getCartContext,
  getCartErrorMessage,
  getEstimatedTaxState,
  getProductsForAnalytic,
  getProgramIdListForAnalytic,
  isCartEmpty,
  isCartInvalid,
} from '../cart/shop-cart/store/shop-cart.selectors';
import { Product, ShopCart } from '../cart/shop-cart/interfaces/shop-cart.interface';
import cardLogoStyles from '../../common/styles/card-logo-styles';
import variables from '../../common/styles/variables';
import { BaseComponent } from '../../features/base-component/base-component';
import { booleanConverter } from '../../common/converters';
import { UserProfile } from '../user-profile/user-profile.interface';
import { loading, smallSpinner } from '../../common/constants/api-constants';
import { MessageType } from '../alert/global-message.interface';
import { AlertSize } from '../alert/alert';
import { getCheckoutUrl } from '../../common/utils/cart-util';
import { getBraintreeClientTokenApiStatus, getClientTokenResponse, getPoOrderNumber, isProcessingPaypalInfo, isPurchaseOrderApplied, isPurchaseOrderSelected, updatePurchaseOrderDetails } from './shop-checkout';
import { getUserDataForUserAnalytics, isSuccessUser, selectUser } from '../user-profile/user-profile.selectors';
import { ActionStatus } from '../../common/constants/action-status';
import { getLoadPaypalSdkStatus } from '../payment-methods/braintree';
import emit from '../../common/events';
import { getPromoCode } from '../promo-code/promo-code.selectors';
import { getShopCart } from '../cart/shop-cart/thunks/get-shop-cart.thunk';
import { isDesktop } from '../../common/utils/util';
import { are3rdPartyPaymentsEnabled } from '../payment-methods/utils/braintree.util';
import { ClientTokenDetails, PurchaseOrderDetails } from './shop-checkout.interface';
import {
  getAdditionalOrderAnalytics,
  getAdditionalSearchTermAnalytics,
  getProductsWithAdditionalAnalytics,
} from '../../common/utils/analytics-util';
import { deleteRelatedProductMapper, filterCartItemsOnCheckoutStep } from '../search/utils/search.util';
import { savePurchaseOrder } from '../cart/shop-cart/thunks/store-purchase-order.thunk';
import { PO_ERROR_MESSAGE } from '../payment-methods/utils/po-order.util';
import { getCartAnalyticsOverrides } from '../cart/shop-cart/shop-cart.analytics';

/**
 * An element to go to the checkout page
 *
 * @csspart link - The link
 * @csspart container - The container of the link
 * 
 * @event click - A user has clicked the link to checkout.
 *        <br><br>&cross; dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: n/a
 * @event go-to-checkout - A user has clicked the link to checkout
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { cart }
 */
@customElement('mhecomm-checkout')
export class Checkout extends connect(store)(BaseComponent) {

  static styles = [variables, cardLogoStyles];

  /**
   * The text to display when there is at least one item in the cart.
   */
  @property()
  text = 'Checkout';

  /**
   * If true, it will require recaptcha to pass in order to proceed.
   */
  @property({ converter: booleanConverter })
  recaptcha = false;

  /**
   * The text to display when the cart is empty.
   */
  @property()
  emptyText = 'Your cart is empty';

  /**
   * If set to true, the user will be sent to the checkout page even if there cart is empty.
   */
  // TODO: check if we still need this.
  @property({ converter: booleanConverter })
  ignoreEmptyCart = false;

  /**
   * The store to checkout to. If not set the national checkout will be used.
   */
  @property()
  store?: string

  /**
   * If set to true and user is not login, the user will be sent to login page.
   * If set to true and user is loggedin, the user will be sent to the checkout page.
   * Otherwise cart page default.
   */
  @property({ converter: booleanConverter })
  checkout = false;

  /**
   * The button classes.
   */
  @property()
  btnClass = 'btn btn-lg btn-primary';

  /**
   * The button div classes.
   */
  @property()
  btnDivClass = 'd-grid';

  @state()
  private cart: ShopCart | undefined;

  @state()
  protected user?: UserProfile;

  @state()
  private someProductsAreUnavailable = false;

  @state()
  private isProcessing = false;

  @state()
  private show3dPartyPaymentButtons = false;

  @state()
  private showPoErrorMsg = false;

  private isCartEmpty = false;

/**
   * Flag to indicate whether checkout button is loading
   */
  @state()
  private isLoading = false;

  private captureProceedToCheckoutAnalytics = false;

  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);

    // TODO: Pass store code
    await store.dispatch(getShopCart({}));
  }

  renderComp() {
    const storeState = store.getState() as StoreState;
    const clientTokenResponse: ClientTokenDetails | undefined = getClientTokenResponse(storeState);

    return html`${this.isLoading ? loading() : html`
    <div class="${this.btnDivClass} gap-2">
      ${isCartInvalid(storeState) && getCartErrorMessage(storeState) ? 
        html`<mhecomm-alert
          id="large-cart-error-message"
          type=${MessageType.danger}
          size=${AlertSize.small}
          message=${getCartErrorMessage(storeState)!}
          data-automation-id="invalid-cart"
        >
        </mhecomm-alert>`: nothing
      }
      <button 
        id="btn-${this.id}" 
        class=${this.linkClasses()} 
        aria-describedby="${this.showPoErrorMsg ? 'purchase-order-error-message' : ''}" 
        ?disabled="${this.isProcessing}"
        @click="${this._onClick}"
      >
        ${this.linkText()}&nbsp;${ this.isProcessing ? smallSpinner() : ''}
      </button>
      ${this.showPoErrorMsg
        ? html `
            <mhecomm-alert
              id="purchase-order-error-message"
              type="${MessageType.danger}"
              size=${AlertSize.small}
              ?dismissible="${false}"
              message=${PO_ERROR_MESSAGE}
            ></mhecomm-alert>`
        : nothing
      }
      
    </div>  

    ${this.user && !this.isCartEmpty && this.text === "Proceed to checkout" && !this.someProductsAreUnavailable ? html`
      <div class="d-flex flex-column p-2 rounded mt-2 gap-1 bg-light">
          <div class="d-flex flex-row align-items-center gap-1">
            ${this.uxtIconReady ? html`<uxt-icon icon="lock-fill" class="checkout-with-icon"></uxt-icon>` : ''}
            <span>Checkout with:</span>
          </div>
          <ul class="d-flex flex-row flex-wrap align-items-center list-unstyled m-0 gap-2">
            ${this.cart && this.cart?.showPoOption ? html`
              <li class="cart-card-po card-frame d-flex justify-content-center align-items-center bg-white">
                <span class="cart-card-po-label fw-bold text-dark">Purchase Order</span>
              </li>
            ` : nothing}
            ${isDesktop() && clientTokenResponse?.venmoEnabled && clientTokenResponse?.venmoProfileId ? html`
              <li class="cart-card card-frame d-none d-xl-flex justify-content-center align-items-center bg-white">
                <img src="/uxt/static/external/misc/logo-venmo.svg" class="w-100 h-100" alt="Venmo" />
              </li>
            ` : nothing}
            <li class="cart-card card-frame d-flex justify-content-center align-items-center bg-white">
              <img src="/uxt/static/external/misc/logo-visa.svg" class="cart-visa-logo" alt="Visa" />
            </li>
            <li class="cart-card card-frame d-flex justify-content-center align-items-center bg-white">
              <img src="/uxt/static/external/misc/logo-mastercard.svg" class="cart-mastercard-logo" alt="Mastercard" />
            </li>
            <li class="cart-card-amex card-frame d-flex justify-content-center align-items-center">
              <img src="/uxt/static/external/misc/logo-am-ex-inline.svg" class="w-100 h-100" alt="American Express" />
            </li>
            <li class="cart-card card-frame d-flex justify-content-center align-items-center bg-white">
              <img src="/uxt/static/external/misc/logo-discover.svg" class="cart-discover-logo" alt="Discover" />
            </li>
          </ul>
      </div>
      ${this.show3dPartyPaymentButtons ? html`
          <div class="d-flex fw-bold justify-content-center mt-3">Or Checkout Faster With:</div>` : nothing}

      ` : nothing}
             
    `}`;

  }

  private linkClasses() {
    const btnClassMap = {};
    this.btnClass.split(' ').map(function(val){
      btnClassMap[val] = true;
    });
    return classMap({
      'disabled': this.shouldDisable(),
      'd-none': this.isHide(),
      ...btnClassMap
    });
  }

  update(changedProperties: any) {
    super.update(changedProperties);
  }

  private shouldDisable() {
    const invalidCart = isCartInvalid(store.getState() as StoreState);
    return this.checkout && (invalidCart || this.someProductsAreUnavailable);
  }

  stateChanged(state: StoreState) {
    this.someProductsAreUnavailable = areProductsUnavailable(state);
    this.cart = getCart(state);
    if (isSuccessUser(state)) {
      this.user = selectUser(state);
      this.show3dPartyPaymentButtons = are3rdPartyPaymentsEnabled(state) && !isPurchaseOrderSelected(state);
    }
    this.isCartEmpty = isCartEmpty(state);
    this.isLoading = this.checkout && !!this.user && (this.isLoadingBraintree() || this.isLoadingPaypalSdk())
    this.isProcessing = this.checkout && !!isProcessingPaypalInfo(state);

    if (!isPurchaseOrderSelected(state) || isPurchaseOrderApplied(state)) {
      this.resetPoErrorMsg();
    }
    
  }

  resetPoErrorMsg() {
    this.showPoErrorMsg = false;
  }

  isLoadingBraintree() {
    const status = getBraintreeClientTokenApiStatus(store.getState() as StoreState);
    return status === ActionStatus.loading;
  }

  /**
   *
   * @returns Checking by the paypal payment method component to avoid
   * the re-render on the cart page.
   */
  isLoadingPaypalSdk() {
    return getLoadPaypalSdkStatus(store.getState() as StoreState) === ActionStatus.loading;
  }

  linkText() {
    return this.isHide() ? this.emptyText : this.text;
  }

  /**
   * Whether or not the checkout link is currently in a hide state.
   */
  isHide() {
    if (this.ignoreEmptyCart) {
      return false;
    } else {
      return ((this.cart != undefined && this.cart.products.length === 0));
    }
  }

  async _onClick() {
    const state = store.getState() as StoreState;
    const context = getCartContext(state, this.store);
    const isLoggedIn = context.isLoggedIn;
    this.store = this.store ? this.store : context.storeCode;
    //redirectUrl is loginUrl or checkoutUrl or continue shopping landing page url
    const redirectUrl = getCheckoutUrl(state, this.store);
    const list = getProgramIdListForAnalytic(state);
    if (!isLoggedIn && redirectUrl) {
      //redirectUrl is loginUrl
      if (!isLoggedIn && this.checkout) {
        window.sessionStorage.clear();
      }
      this.captureProceedToCheckoutAnalytics = true;
      this.filterItemsFromCartLocalStorageNotPresentInCart(list!);
      this.removeRelatedProductMapperFromLS();
      this.emit('proceed-to-checkout', redirectUrl);
      window.location.href = redirectUrl;
    } else if (!redirectUrl) {
      console.error('No checkoutUrl found');
    } else {
      const purchaseOrderDetails: PurchaseOrderDetails = {
        purchase_order_number: getPoOrderNumber(state)! || "",
        is_purchase_order: getPoOrderNumber(state) ? true : false
      };
      this.captureProceedToCheckoutAnalytics = true;
      this.filterItemsFromCartLocalStorageNotPresentInCart(list!);
      this.removeRelatedProductMapperFromLS();

      this.showPoErrorMsg = isPurchaseOrderSelected(state) && !isPurchaseOrderApplied(state) ? true : false;

      if (this.showPoErrorMsg) {
        this.emitError(PO_ERROR_MESSAGE);
      }

      store.dispatch(updatePurchaseOrderDetails(purchaseOrderDetails));

      this.emit('go-to-checkout', redirectUrl);

      if (!isPurchaseOrderSelected(state) || isPurchaseOrderApplied(state)) {
        await store.dispatch(savePurchaseOrder(purchaseOrderDetails));

        //redirectUrl is checkoutUrl
        location.href = redirectUrl;
      }
    }
  }

  emitError(label: string): void {
    emit({
      element: this,
      name: 'Error',
      detail: {
        label
      }
    });
  }

  emit(name: string, checkoutUrl: string) {
    emit({
      element: this,
      name: name,
      detail: this.dataLayerDetail(name, checkoutUrl)
    });
    this.captureProceedToCheckoutAnalytics = false;
  }

  private dataLayerDetail(name: string, checkoutUrl: string) {
    const state = store.getState() as StoreState;
    const cart = getCart(state);
    return {
      label: this.linkText(),
      quantity: this.getProductCount(cart?.products),
      cart: {
        ...this.cart,
        products: getProductsWithAdditionalAnalytics({
          products: this.cart?.products ?? [],
        }),
      },
      store: this.store,
      href: checkoutUrl,
      url: window.location.href,
      user: getUserDataForUserAnalytics(state, true),
      isLogged: isSuccessUser(state),
      products: getProductsForAnalytic(state),
      promocode: getPromoCode(state),
      estimateTax: getEstimatedTaxState(state)?.totalTax?.amount,
      isInstructorComp: 'no',
      ...getAdditionalOrderAnalytics({
        products: cart?.products,
      }),
      ...getAdditionalSearchTermAnalytics({
        captureProceedToCheckoutAnalytics: this.captureProceedToCheckoutAnalytics,
      }),
      isEdoms: !!cart?.metaOfferId,
      ...(name === 'go-to-checkout' && !!getPoOrderNumber(state) && isPurchaseOrderApplied(state) && 
      { 
        paymentType: "Purchase Order", 
        purchaseOrderNumber: getPoOrderNumber(state) 
      }),
      ...getCartAnalyticsOverrides(state),
    };
  }

  getProductCount(products: Product[] | undefined) {
    return products ? products.map(p => p.quantity).reduce((sum, qty) => sum + qty, 0) : 0;
  }

  private filterItemsFromCartLocalStorageNotPresentInCart(list: string[]) {
    filterCartItemsOnCheckoutStep(list);
  }

  private removeRelatedProductMapperFromLS() {
    deleteRelatedProductMapper();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-checkout': Checkout;
  }
}
